/**
 * RichBody — shared Markdown renderer for public article / custom-page bodies.
 *
 * Extracted from src/pages/ArticleDetail.tsx `renderMarkdown()` so the custom
 * page renderer uses the same block-level Markdown + fenced-html (video /
 * iframe) + DOMPurify + trusted-embed allowlist. ArticleDetail is now a thin
 * consumer that imports this component.
 *
 * Block rules mirror ArticleDetail exactly (byte-identical output):
 *   - `<!-- template-fields:{...} -->` header comment stripped
 *   - ```html fences → renderVideoFence()
 *   - `---` → <hr>
 *   - `## ` / `### ` → ShortcodeBlock (h2/h3)
 *   - `> ` → <blockquote>
 *   - `- ` / `* ` / `1. ` lists → <ul>/<ol>
 *   - everything else → <p> via ShortcodeBlock
 *
 * Inline Markdown walks renderInlineMarkdownWithShortcodes with a per-module
 * image class. Image fences that came from TinyMCE render as `![alt](url)`.
 */
import { useMemo } from 'react'
import DOMPurify from 'dompurify'
import { isTrustedEmbedUrl } from '@/admin/lib/video-urls'
import { renderInlineMarkdownWithShortcodes } from '@/utils/inlineMarkdown'
import ShortcodeBlock from '@/components/embed/ShortcodeBlock'

interface Props {
  /** Markdown wire format (cms_articles.body_md_* / cms_custom_pages.body_md_*). */
  markdown: string
  /** class applied to inline images rendered inside paragraphs. */
  imageClassName?: string
}

export default function RichBody({ markdown, imageClassName }: Props): React.ReactNode {
  const fences = useMemo(() => {
    const out: string[] = []
    const fenceRe = /```html\n([\s\S]*?)\n```/g
    const cleaned = markdown.replace(/^<!-- template-fields:[\s\S]*?-->\s*/, '')
    const stripped = cleaned.replace(fenceRe, (_m, body: string) => {
      out.push(body.trim())
      return `\n\n__FENCE_${out.length - 1}__\n\n`
    })
    return { fences: out, blocks: stripped.split(/\n{2,}/) }
  }, [markdown])

  function renderVideoFence(rawHtml: string): React.ReactNode {
    // 2026-10-12: DOMPurify strips event handlers and script tags before we
    // look at the parsed tree; isTrustedEmbedUrl then ensures the iframe src
    // is one of the known video providers — anything outside that list is
    // silently dropped, so a hand-edited body_md can't exfiltrate via a
    // malicious embed URL. The existing <video> path is preserved as-is.
    if (typeof window === 'undefined') return null
    const clean = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['video', 'iframe', 'source'],
      ALLOWED_ATTR: [
        'src',
        'type',
        'width',
        'height',
        'controls',
        'allow',
        'allowfullscreen',
        'frameborder',
        'style',
      ],
    })
    const doc = new DOMParser().parseFromString(clean, 'text/html')
    const videos = Array.from(doc.querySelectorAll('video'))
    const iframes = Array.from(doc.querySelectorAll('iframe'))
    if (videos.length === 0 && iframes.length === 0) return null
    return (
      <div className="my-4 space-y-3">
        {videos.map((v, i) => {
          const sources = Array.from(v.querySelectorAll('source'))
          const src = v.getAttribute('src') || sources[0]?.getAttribute('src') || ''
          return (
            <video
              key={`v${i}`}
              src={src || undefined}
              controls={v.hasAttribute('controls')}
              className="block w-full rounded-yada-md border border-border shadow-yada"
            />
          )
        })}
        {iframes.map((f, i) => {
          const src = f.getAttribute('src') ?? ''
          if (!isTrustedEmbedUrl(src)) return null
          const allow =
            f.getAttribute('allow') ??
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          return (
            <iframe
              key={`i${i}`}
              src={src}
              allow={allow}
              allowFullScreen={f.hasAttribute('allowfullscreen')}
              loading="lazy"
              className="block w-full aspect-video rounded-yada-md border border-border shadow-yada"
            />
          )
        })}
      </div>
    )
  }

  function inlineRender(s: string): React.ReactNode {
    return renderInlineMarkdownWithShortcodes(s, { imageClassName })
  }

  return (
    <>
      {fences.blocks.map((raw, idx) => {
        const text = raw.trim()
        if (!text) return null
        const fenceHit = text.match(/^__FENCE_(\d+)__$/)
        if (fenceHit) {
          return (
            <div key={`f${idx}`}>
              {renderVideoFence(fences.fences[Number(fenceHit[1])] ?? '')}
            </div>
          )
        }
        if (text === '---') return <hr key={idx} className="my-6 border-border" />
        if (text.startsWith('## ')) {
          return (
            <ShortcodeBlock
              key={idx}
              as="h2"
              className="text-2xl font-bold mt-8 mb-3 text-ink"
              text={text.slice(3)}
              options={{ imageClassName }}
            />
          )
        }
        if (text.startsWith('### ')) {
          return (
            <ShortcodeBlock
              key={idx}
              as="h3"
              className="text-xl font-bold mt-6 mb-2 text-ink"
              text={text.slice(4)}
              options={{ imageClassName }}
            />
          )
        }
        if (text.startsWith('> ')) {
          return (
            <blockquote key={idx} className="border-l-4 border-primary pl-4 my-4 italic text-ink-soft">
              {inlineRender(text.slice(2))}
            </blockquote>
          )
        }
        if (/^[-*]\s+/.test(text) || /^\d+\.\s+/.test(text)) {
          const isOrdered = /^\d+\.\s+/.test(text)
          const Tag = isOrdered ? 'ol' : 'ul'
          const items = text.split(/\n/).filter((l) => /^([-*]|\d+\.)\s+/.test(l))
          return (
            <Tag key={idx} className="list-disc pl-6 my-4 space-y-1">
              {items.map((line, i) => {
                const stripped = line.replace(/^([-*]|\d+\.)\s+/, '')
                return <li key={i}>{inlineRender(stripped)}</li>
              })}
            </Tag>
          )
        }
        return (
          <ShortcodeBlock
            key={idx}
            as="p"
            className="mb-4 leading-relaxed"
            text={text}
            options={{ imageClassName }}
          />
        )
      })}
    </>
  )
}
